"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

const STORAGE_KEY = "punkrecords-cookie-notice-dismissed";

type BannerState = "pending" | "visible" | "hidden";

export function CookieBanner() {
  const [state, setState] = useState<BannerState>("pending");

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY) === "1";
      setState(dismissed ? "hidden" : "visible");
    } catch {
      setState("visible");
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setState("hidden");
  };

  if (state !== "visible") return null;

  return (
    <div
      id="cookie-notice-bar"
      role="region"
      aria-label="Cookie and storage notice"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
    >
      <div
        className="pointer-events-auto flex max-w-4xl flex-col gap-3 rounded-lg border border-slate-700/80 bg-slate-900/95 px-4 py-3 text-xs text-slate-300 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:gap-4 sm:text-sm"
      >
        <p className="min-w-0 flex-1 leading-relaxed">
          We use{" "}
          <strong className="font-medium text-slate-200">Google Analytics</strong>{" "}
          to understand site traffic and your browser’s{" "}
          <strong className="font-medium text-slate-200">local storage</strong>{" "}
          for things like theme and saved lists.{" "}
          <Link
            href="/cookies"
            className="whitespace-nowrap text-blue-400 underline-offset-2 hover:underline"
          >
            More about cookies &amp; storage
          </Link>
        </p>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700 sm:text-sm"
          >
            Got it
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-300"
            aria-label="Close notice"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-5 w-5"
              aria-hidden
            >
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
